import { usePathname } from 'next/navigation';

import {
  IconDeAppStore,
  IconDePlayStore,
  IconEnAppStore,
  IconEnPlayStore,
  IconFrAppStore,
  IconFrPlayStore,
  IconItAppStore,
  IconItPlayStore,
} from './app-badge-icons/appBadgeIcons';

import {
  ActivityTrackerEventType,
  routePaths,
} from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { useCurrentRoute } from '@/modules/activityTracker/useCurrentRoute';
import { APP_STORE_URL, PLAY_STORE_URL } from '@/modules/branch/constants';
import { DeviceType } from '@/modules/device/types';
import { useUserAgent } from '@/modules/device/useUserAgent';
import { useBranchAppLink } from '@/modules/externalTracking/useBranchAppLink';
import { useExternalTracking } from '@/modules/externalTracking/useExternalTracking';
import { useCurrentLanguage } from '@/modules/language/useCurrentLanguage';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

const translatedIosIcons = {
  en: IconEnAppStore,
  de: IconDeAppStore,
  fr: IconFrAppStore,
  it: IconItAppStore,
};

const translatedAndroidIcons = {
  en: IconEnPlayStore,
  de: IconDePlayStore,
  fr: IconFrPlayStore,
  it: IconItPlayStore,
};

export function useAppBadgesMethods(directStoreLinks: boolean = false) {
  const { language } = useCurrentLanguage();
  const { device } = useUserAgent();
  const [, currentUser] = useCurrentUser();
  const { sendActivityTrackerEvent: sendATEvent } = useActivityTracker({
    userId: currentUser?.id,
    overrides: { transitionFrom: routePaths.HOME_PAGE.route },
  });
  const { gtm } = useExternalTracking();
  const page = useCurrentRoute();
  const urlPath = usePathname();
  const branchAppLink = useBranchAppLink();

  const useDirectLinks = directStoreLinks && device === DeviceType.GENERAL;

  function getIosAppBadge() {
    return {
      href: useDirectLinks ? APP_STORE_URL : branchAppLink,
      badge: (translatedIosIcons[language] ?? IconEnAppStore)({ title: 'app' }),
    };
  }

  function getAndroidAppBadge() {
    return {
      href: useDirectLinks ? PLAY_STORE_URL : branchAppLink,
      badge: (translatedAndroidIcons[language] ?? IconEnPlayStore)({
        title: 'play',
      }),
    };
  }

  function handleBadgeClick(store: DeviceType, trackingId: string) {
    sendATEvent(ActivityTrackerEventType.APP_DOWNLOAD_ACTION, {
      page,
      urlPath,
      ctaId: trackingId,
      schemaVersion: '1.0',
    });

    if (store === DeviceType.IOS) {
      gtm.sendEvent({
        category: 'App Store',
        label: 'seller hero',
        action: 'click app store link',
      });
    } else {
      gtm.sendEvent({
        category: 'App Store',
        label: 'seller hero',
        action: 'click play store link',
      });
    }
  }

  return {
    getIosAppBadge,
    getAndroidAppBadge,
    handleBadgeClick,
  };
}
